import { motion, useScroll, useTransform } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import {
  ArrowRight, Cpu, Network, ShieldCheck, Truck, Wrench,
  CheckCircle2, XCircle, Sparkles, Building2, Activity, Play,
  Zap, BadgeCheck, Phone, Newspaper, Calendar, Boxes,
  Droplets, HardHat, FlaskConical, Stethoscope, Ear,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import LiveDispatchReel from "@/components/LiveDispatchReel";
import DispatchTicker from "@/components/DispatchTicker";

const STERILEMATE_LOGO =
  "/logos/sterilemate-white.png?v=4";
const ASKISAAC_LOGO =
  "/logos/askisaac-b-monogram.svg";

/* ----- Imagery curated from the design-agent brief ----- */
const IMG_SURGICAL =
  "https://images.unsplash.com/photo-1688565631957-0306970fdd74?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwyfHxzdXJnaWNhbCUyMHRvb2xzJTIwY2xvc2UlMjB1cHxlbnwwfHx8fDE3NzY5ODQ3Mjl8MA&ixlib=rb-4.1.0&q=85";
const IMG_NETWORK =
  "https://images.unsplash.com/photo-1771793231925-25b44b91a469?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMG5ldHdvcmslMjBkYXJrJTIwY3lhbnxlbnwwfHx8fDE3NzY5ODQ3Mjl8MA&ixlib=rb-4.1.0&q=85";
const IMG_WAREHOUSE =
  "https://images.pexels.com/photos/5860937/pexels-photo-5860937.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const IMG_DATA =
  "https://images.pexels.com/photos/27141316/pexels-photo-27141316.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const TRUST_ITEMS = [
  "Certified Small Business", "Certified Small Business", "CAGE 9GH66", "DUNS 118062870",
  "ICRA Compliant", "AAMI ST108 Ready", "Joint Commission Ready",
  "Private Sector", "Local Government", "Federal Government",
  "OSHA Healthcare Certified", "Licensed GC (TX)",
];

const FADE_UP = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

const STAGGER = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12 } },
};

// Phase-2 motion variants
const SLIDE_LEFT = {
  hidden: { opacity: 0, x: -60 },
  show: { opacity: 1, x: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};
const SLIDE_RIGHT = {
  hidden: { opacity: 0, x: 60 },
  show: { opacity: 1, x: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};
const ROW_STAGGER = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06, delayChildren: 0.1 } },
};
const ROW_FADE = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

// ----- Animated number counter (counts up from 0 → target on viewport entry) -
function useCountUp(target, { duration = 1400, decimals = 0, start = false } = {}) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!start) return;
    const t0 = performance.now();
    let raf = 0;
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / duration);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - p, 3);
      const v = eased * target;
      setValue(decimals === 0 ? Math.round(v) : parseFloat(v.toFixed(decimals)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, decimals, start]);
  return value;
}

function AnimatedStat({ value, suffix = "", prefix = "", label, start, decimals = 0 }) {
  const v = useCountUp(value, { start, decimals });
  const display = decimals === 0
    ? v.toLocaleString()
    : v.toFixed(decimals);
  return (
    <div className="border-l-2 border-cyan-400/40 pl-3">
      <div className="font-display text-2xl md:text-3xl font-bold text-white tracking-tight tabular-nums">
        {prefix}{display}{suffix}
      </div>
      <div className="text-[11px] text-slate-400 font-sans uppercase tracking-wider mt-1">
        {label}
      </div>
    </div>
  );
}

// ----- Typewriter rotator for the live-status banner --------------------------
const STATUS_MESSAGES = [
  "Quote received · 37s ago",
  "PO #4521 invoiced · 2m ago",
  "Compliance check passed · 5m ago",
  "Tech dispatched · Bayshore General · 8m ago",
];
function useTypewriter(messages, { typeMs = 38, pauseMs = 2400, eraseMs = 22 } = {}) {
  const [text, setText] = useState("");
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    let cancelled = false;
    let timeoutId;
    const target = messages[idx % messages.length];
    let i = 0;
    const type = () => {
      if (cancelled) return;
      if (i <= target.length) {
        setText(target.slice(0, i));
        i++;
        timeoutId = setTimeout(type, typeMs);
      } else {
        timeoutId = setTimeout(erase, pauseMs);
      }
    };
    const erase = () => {
      if (cancelled) return;
      if (i >= 0) {
        setText(target.slice(0, i));
        i--;
        timeoutId = setTimeout(erase, eraseMs);
      } else {
        setIdx((x) => x + 1);
      }
    };
    type();
    return () => { cancelled = true; clearTimeout(timeoutId); };
  }, [idx, messages, typeMs, pauseMs, eraseMs]);
  return text;
}

// ----- Cursor spotlight (only on pointer-fine devices) ------------------------
function CursorSpotlight({ targetRef }) {
  useEffect(() => {
    const el = targetRef.current;
    if (!el) return;
    if (window.matchMedia && !window.matchMedia("(pointer: fine)").matches) return;
    let raf = 0;
    let queuedX = 50, queuedY = 30;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const x = ((e.clientX - r.left) / r.width) * 100;
      const y = ((e.clientY - r.top) / r.height) * 100;
      queuedX = x; queuedY = y;
      if (raf) return;
      raf = requestAnimationFrame(() => {
        el.style.setProperty("--spot-x", `${queuedX}%`);
        el.style.setProperty("--spot-y", `${queuedY}%`);
        raf = 0;
      });
    };
    el.addEventListener("pointermove", onMove);
    return () => {
      el.removeEventListener("pointermove", onMove);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [targetRef]);
  return <div className="hero-spotlight" aria-hidden="true" />;
}

// ============================================================================
// HERO
// ============================================================================
export function HomeHeroV2({ onRequestDemo, onJoinNetwork }) {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const [statsInView, setStatsInView] = useState(false);
  const liveStatus = useTypewriter(STATUS_MESSAGES);

  // Rotating hero photography — cinematic cross-fade like McKesson.
  // Curated hospital-only imagery: diverse medical teams (Black + white,
  // male + female doctors, nurses, EMTs) + hospital corridors + a
  // modern hospital exterior. Every frame is medical/clinical — no
  // stock landscapes, mountains, or generic office imagery.
  const HERO_FRAMES = [
    "/api/static/email_assets/hero_black_surgeon.jpg",                                                    // Black surgeon in OR (AI-generated, own asset)
    "/api/static/email_assets/hero_hospital_building.jpg",                                                 // Modern hospital building (aerial close-up, AI-generated)
    "https://images.pexels.com/photos/8460047/pexels-photo-8460047.jpeg?auto=compress&cs=tinysrgb&w=2400", // Diverse OR surgical team (multi-ethnic)
    "https://images.pexels.com/photos/7089336/pexels-photo-7089336.jpeg?auto=compress&cs=tinysrgb&w=2400", // Diverse doctors reviewing brain scan (Black + white male)
    "https://images.pexels.com/photos/4173251/pexels-photo-4173251.jpeg?auto=compress&cs=tinysrgb&w=2400", // Medical staff walking down hospital corridor
    "https://images.pexels.com/photos/7108117/pexels-photo-7108117.jpeg?auto=compress&cs=tinysrgb&w=2400", // Ambulance / EMT arrival
  ];
  // Bespoke ambient hero video — Sora 2 generated 12s corporate corridor loop.
  // Hosted at /api/static/email_assets/hero_ambient.{webm,mp4} — WebM/VP9 for
  // widest compatibility (Chromium, Firefox), MP4/H.264 fallback (Safari).
  const HERO_VIDEO = "/api/static/email_assets/hero_ambient.webm";
  const HERO_VIDEO_MP4 = "/api/static/email_assets/hero_ambient.mp4";
  const [heroFrame, setHeroFrame] = useState(1);
  const [videoReady, setVideoReady] = useState(false);
  useEffect(() => {
    // Frame 0 = video (holds for ~10s to let the loop breathe).
    // Frames 1..N = still images (each 5.2s).
    const isVideoFrame = heroFrame === 0 && HERO_VIDEO && videoReady;
    const next = () =>
      setHeroFrame((f) => {
        if (HERO_VIDEO) {
          const raw = (f + 1) % (HERO_FRAMES.length + 1);
          // Skip the video slot until the video is actually ready to play,
          // otherwise the hero briefly goes blank.
          if (raw === 0 && !videoReady) return 1;
          return raw;
        }
        // No video: cycle through images 1..N
        const nextIdx = f + 1;
        return nextIdx > HERO_FRAMES.length ? 1 : nextIdx;
      });
    const t = setTimeout(next, isVideoFrame ? 10000 : 5200);
    return () => clearTimeout(t);
  }, [heroFrame, videoReady]);

  // Parallax
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  const BIG_FADE = {
    hidden: { opacity: 0, y: 60 },
    show: { opacity: 1, y: 0, transition: { duration: 1.1, ease: [0.16, 1, 0.3, 1] } },
  };
  const BIG_STAGGER = {
    hidden: {},
    show: { transition: { staggerChildren: 0.22, delayChildren: 0.15 } },
  };

  return (
    <section
      ref={heroRef}
      className="relative overflow-hidden bg-[#0a1628] min-h-[92vh] flex items-center"
      data-testid="home-hero-v2"
    >
      {/* Cinematic cross-fading hero photography stack */}
      <motion.div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{ y: heroY, scale: heroScale }}
      >
        {/* Video frame — shows only during slot 0 when a URL is configured. */}
        {HERO_VIDEO && (
          <motion.video
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            onCanPlay={() => setVideoReady(true)}
            onError={() => setVideoReady(false)}
            initial={false}
            animate={{ opacity: heroFrame === 0 && videoReady ? 1 : 0, scale: heroFrame === 0 ? 1.04 : 1.0 }}
            transition={{ opacity: { duration: 1.4, ease: "easeInOut" }, scale: { duration: 10, ease: "linear" } }}
            className="absolute inset-0 w-full h-full object-cover"
            data-testid="hero-video"
          >
            <source src={HERO_VIDEO} type="video/webm" />
            <source src={HERO_VIDEO_MP4} type="video/mp4" />
          </motion.video>
        )}
        {HERO_FRAMES.map((src, i) => (
          <motion.img
            key={src}
            src={src}
            alt=""
            initial={false}
            animate={{ opacity: heroFrame === i + 1 ? 1 : 0, scale: heroFrame === i + 1 ? 1.06 : 1.0 }}
            transition={{ opacity: { duration: 1.4, ease: "easeInOut" }, scale: { duration: 6, ease: "linear" } }}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-[#061a33] via-[#061a33]/85 via-40% to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0a1628]" />
      </motion.div>

      {/* Frame indicator dots — video slot first (if enabled), then still frames */}
      <div className="absolute bottom-8 right-8 z-20 hidden md:flex items-center gap-1.5" data-testid="hero-frame-dots">
        {HERO_VIDEO && (
          <button
            onClick={() => setHeroFrame(0)}
            aria-label="Hero video"
            className={`h-1 rounded-full transition-all duration-500 flex items-center justify-center ${
              heroFrame === 0 ? "w-10 bg-cyan-300" : "w-5 bg-white/25 hover:bg-white/50"
            }`}
          >
            {heroFrame === 0 && <Play className="w-2 h-2 text-[#061124]" />}
          </button>
        )}
        {HERO_FRAMES.map((_, i) => (
          <button
            key={i}
            onClick={() => setHeroFrame(i + 1)}
            aria-label={`Hero frame ${i + 2}`}
            className={`h-1 rounded-full transition-all duration-500 ${
              heroFrame === i + 1 ? "w-8 bg-cyan-300" : "w-4 bg-white/25 hover:bg-white/50"
            }`}
          />
        ))}
      </div>

      {/* Subtle atmospheric wash */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 left-1/4 w-[720px] h-[720px] bg-cyan-500/12 blur-[160px] rounded-full" />
        <div className="absolute bottom-0 right-0 w-[560px] h-[560px] bg-amber-400/8 blur-[140px] rounded-full" />
      </div>

      <CursorSpotlight targetRef={heroRef} />

      <div className="relative z-10 w-full max-w-[1440px] mx-auto px-6 lg:px-12 py-24 lg:py-32">
        <motion.div
          initial="hidden"
          animate="show"
          variants={BIG_STAGGER}
          className="max-w-4xl"
        >
          <motion.div variants={BIG_FADE} className="mb-8 inline-flex items-center gap-3">
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/15 border border-cyan-400/30 backdrop-blur">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-cyan-300" />
              </span>
              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-cyan-200">
                Built for U.S. Hospitals & Health Systems
              </span>
            </div>
          </motion.div>

          <motion.h1
            variants={BIG_FADE}
            className="font-display text-white font-bold leading-[0.98] tracking-tight text-[3.5rem] sm:text-[4.5rem] lg:text-[6rem] xl:text-[7rem]"
            data-testid="hero-headline"
          >
            Supplied. Built.<br />
            <span className="text-cyan-300">Tracked.</span>
          </motion.h1>

          <motion.p
            variants={BIG_FADE}
            className="mt-10 text-xl md:text-2xl leading-[1.5] text-slate-200/90 font-sans max-w-3xl"
          >
            <span className="text-white font-semibold">Sterile Mate</span> is a surgical,
            sterile processing and janitorial supplier, a healthcare infrastructure partner,
            and a healthcare software provider &mdash; three capabilities most health systems
            buy from a dozen different vendors.
          </motion.p>

          <motion.ul
            variants={BIG_FADE}
            className="mt-6 space-y-3 text-lg md:text-xl leading-[1.55] text-slate-200/90 font-sans max-w-3xl"
            data-testid="hero-pillars-list"
          >
            <li className="flex gap-3">
              <span aria-hidden="true" className="mt-2 h-2 w-2 rounded-full bg-cyan-300 shrink-0" />
              <span>
                <strong className="text-white font-semibold">Supplies &amp; Disposables:</strong>{" "}
                Surgical instrumentation, sterile processing consumables, EVS and janitorial
                hardware and disposables, medical devices and clinical equipment &mdash;
                dispatched nationwide, no GPO contract required.
              </span>
            </li>
            <li className="flex gap-3">
              <span aria-hidden="true" className="mt-2 h-2 w-2 rounded-full bg-cyan-300 shrink-0" />
              <span>
                <strong className="text-white font-semibold">
                  <Link
                    to="/solutions/general-contracting"
                    className="hover:text-cyan-200 underline decoration-cyan-400/40 hover:decoration-cyan-300 underline-offset-4"
                    data-testid="hero-gc-inline-link"
                  >
                    Healthcare Infrastructure:
                  </Link>
                </strong>{" "}
                Medical-grade water purification &mdash; RO for SPD and dialysis, DI tank
                exchange, facility water treatment to AAMI ST108 &mdash; plus skilled trades,
                critical environment maintenance and turnkey healthcare construction, from
                negative-pressure isolation suites to complete ambulatory surgery centers.
              </span>
            </li>
            <li className="flex gap-3">
              <span aria-hidden="true" className="mt-2 h-2 w-2 rounded-full bg-cyan-300 shrink-0" />
              <span>
                <strong className="text-white font-semibold">
                  <Link
                    to="/solutions/heart"
                    className="hover:text-cyan-200 underline decoration-cyan-400/40 hover:decoration-cyan-300 underline-offset-4"
                    data-testid="hero-software-inline-link"
                  >
                    Healthcare Software:
                  </Link>
                </strong>{" "}
                IMS bill-only implant management, H.E.A.R.T rebate management and inventory
                management &mdash; built for hospital supply chain, priced for the department
                that has to justify it.
              </span>
            </li>
          </motion.ul>

          <motion.p
            variants={BIG_FADE}
            className="mt-6 text-lg md:text-xl leading-[1.5] text-white font-semibold font-sans max-w-3xl"
            data-testid="hero-tagline"
          >
            One partner. One dispatch center. Every case, every corridor, every contract.
          </motion.p>

          {/* Live dispatch ticker — reinforces the delivery-truck intro narrative */}
          <motion.div
            variants={BIG_FADE}
            className="mt-8"
            data-testid="hero-v2-dispatch-ticker-wrap"
          >
            <DispatchTicker />
          </motion.div>

          <motion.div
            variants={BIG_FADE}
            className="mt-8 flex flex-col sm:flex-row flex-wrap gap-4"
          >
            <Button
              onClick={() => navigate("/hospital/login")}
              className="btn-sheen group h-16 px-10 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-[#061124] font-black text-base shadow-[0_16px_50px_-10px_rgba(34,211,238,0.55)] transition-all"
              data-testid="hero-v2-hospitals-btn"
            >
              <Building2 className="w-5 h-5 mr-2" />
              For Hospitals
              <ArrowRight className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" />
            </Button>
            <Button
              onClick={() => navigate("/how-it-works/backorder-sourcing")}
              variant="outline"
              className="h-16 px-10 rounded-xl bg-white/[0.04] backdrop-blur-xl border-white/25 hover:bg-white/[0.10] hover:border-amber-400/50 text-white font-bold text-base transition-all"
              data-testid="hero-v2-backorders-btn"
            >
              <Boxes className="w-5 h-5 mr-2" />
              Backorders Request
            </Button>
            <a
              href="tel:2814563458"
              className="hidden md:inline-flex h-16 px-5 items-center gap-2 text-slate-300 hover:text-white font-sans text-sm transition-colors"
              data-testid="hero-v2-phone"
            >
              <Phone className="w-4 h-4" />
              <span className="font-semibold">281.456.3458</span>
              <span className="text-slate-600">·</span>
              <span>Emergency dispatch</span>
            </a>
          </motion.div>

          <motion.div
            variants={BIG_FADE}
            className="mt-16 grid grid-cols-3 gap-8 max-w-2xl"
            data-testid="hero-v2-stats"
            onViewportEnter={() => setStatsInView(true)}
            viewport={{ once: true, margin: "-80px" }}
          >
            <AnimatedStat value={208} suffix="+" label="Hospitals served nationwide" start={statsInView} />
            <AnimatedStat value={24} suffix="/7" label="Emergency dispatch" start={statsInView} />
            <AnimatedStat value={5} suffix="+ yr" label="Texas Medical Center" start={statsInView} />
          </motion.div>
        </motion.div>
      </div>

      {/* AskIsaac · Live ticker — top-right of hero for immediate visibility */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.6, duration: 0.7 }}
        className="absolute top-24 right-6 md:top-28 md:right-10 z-20 inline-flex items-center gap-3 px-4 py-2.5 rounded-lg bg-white/[0.08] backdrop-blur-md border border-white/[0.15] shadow-2xl shadow-black/40 max-w-[380px]"
        data-testid="hero-live-ticker"
      >
        <div className="relative shrink-0">
          <span className="absolute inset-0 rounded-full bg-emerald-500/50 animate-pulse-soft" />
          <span className="relative block w-2 h-2 rounded-full bg-emerald-400" />
        </div>
        <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-emerald-300 shrink-0">
          AskIsaac · Live
        </span>
        <span
          className="text-[12px] text-slate-200 font-sans truncate typewriter-caret hidden sm:inline"
          data-testid="hero-live-feed"
        >
          {liveStatus}
        </span>
      </motion.div>

      <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-amber-400/55 to-transparent pointer-events-none" />
    </section>
  );
}

// ============================================================================
// AREAS OF FOCUS — McKesson-style 6-card grid, hospital-first, with rotating
// imagery per card. Slots naturally below the hero.
// ============================================================================
export function AreasOfFocus() {
  const navigate = useNavigate();
  const AREAS = [
    {
      key: "sterile-processing",
      label: "Sterile Processing",
      title: "Instrument reprocessing that never misses a case.",
      copy: "SPD-grade detergents, sterilization indicators, pouches, and workflow bundles — ProChek, ProEZ, ProLube, ProView.",
      to: "/sterile-processing",
      images: [
        "/api/static/email_assets/areas_sterile_processing_tech.jpg",
        "/api/static/email_assets/areas_sterile_processing_tech.jpg",
      ],
    },
    {
      key: "water-systems",
      label: "Water & DI Systems",
      title: "AAMI ST108-compliant water on every floor.",
      copy: "RO/DI systems, softeners, 24/7 monitoring, plus the DI Tank Exchange for dialysis, SPD, and pharmacy.",
      to: "/solutions/water-treatment",
      images: [
        "/api/static/email_assets/areas_water_ro_system.jpg",
        "/api/static/email_assets/areas_water_ro_system.jpg",
      ],
    },
    {
      key: "surgical-instruments",
      label: "Surgical Instruments",
      title: "OR-ready, backordered, or discontinued — we source it.",
      copy: "American Surgical authorized distributor. ENT, neuro-sponges, custom trays. Same-day rush and long-tail sourcing.",
      to: "/marketplace/american-surgical",
      images: [
        "/api/static/email_assets/areas_surgical_solutions_team.jpg",
        "/api/static/email_assets/areas_surgical_solutions_team.jpg",
      ],
    },
    {
      key: "evs-janitorial",
      label: "EVS & Janitorial",
      title: "Turn every room over faster. Every terminal clean, every isolation.",
      copy: "Filmop workstations, color-coded microfiber, Green Klean disinfectants, autonomous scrubbers, HEPA vacs.",
      to: "/marketplace/janitorial",
      images: [
        "/api/static/email_assets/areas_evs_janitorial_team.jpg",
        "/api/static/email_assets/areas_evs_janitorial_team.jpg",
      ],
    },
    {
      key: "facilities-trades",
      label: "Facilities & Skilled Trades",
      title: "Licensed GCs, contractors, and PM on demand.",
      copy: "TX-licensed general contracting. HVAC, plumbing, electrical, project management — dispatched through one command center.",
      to: "/solutions/general-contracting",
      images: [
        "/api/static/email_assets/areas_facility_contractor.jpg",
        "/api/static/email_assets/areas_facility_contractor.jpg",
      ],
    },
    {
      key: "medical-devices",
      label: "Medical Devices & Home Health",
      title: "Clinically validated devices. Home-health-ready SKUs.",
      copy: "BPBIO blood pressure kiosks, Vive Precision oximeters/scales, Sol-Care diabetes safety products.",
      to: "/marketplace/medical-devices",
      images: [
        "/api/static/email_assets/areas_home_health_nurse.jpg",
        "/api/static/email_assets/areas_home_health_nurse.jpg",
      ],
    },
    {
      key: "microfiber",
      label: "Microfiber Cleaning",
      title: "70+ SKUs — cloths, wet mops, dust mops, pads, dusters, and handles.",
      copy: "Direct Mop Sales-sourced launderable microfiber: HAI-zone color coding, hospital-grade antimicrobial mops, pocket-mop systems, and pole tools.",
      to: "/marketplace?category=evs-janitorial&sub=Microfiber",
      images: [
        "https://www.directmopsales.com/cdn/shop/files/microfiber-terry-cloths-group-picture_9f5afb99-c405-409a-a6bb-f47192502796.jpg?v=1708689977&width=800",
        "https://www.directmopsales.com/cdn/shop/files/microfiber-wet-mop-group-rough-floor_f8066e16-f62e-4f13-8c40-29815025be61.jpg?v=1723748165&width=800",
      ],
    },
  ];

  return (
    <section
      className="relative bg-white py-24 lg:py-32 overflow-hidden"
      data-testid="areas-of-focus"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-3xl mb-16"
        >
          <p className="text-[11px] font-black uppercase tracking-[3px] text-cyan-700 mb-4">
            Areas of Focus
          </p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.05] tracking-tight">
            Seven operating disciplines.<br />
            <span className="text-slate-400">One command center.</span>
          </h2>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-2xl">
            From Sterile Processing to skilled trades, Sterile Mate is the specialized
            healthcare backbone your hospital already needed. Pick a discipline — we&apos;ll
            handle the rest.
          </p>
        </motion.div>

        {/* 6-card grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {AREAS.map((area, i) => (
            <AreaCard key={area.key} area={area} index={i} onNavigate={navigate} />
          ))}
        </div>
      </div>
    </section>
  );
}

function AreaCard({ area, index, onNavigate }) {
  const [imgIdx, setImgIdx] = useState(0);
  const [hover, setHover] = useState(false);

  // Auto-rotate images every 4s, faster on hover
  useEffect(() => {
    const interval = hover ? 1500 : 4500;
    const t = setInterval(
      () => setImgIdx((i) => (i + 1) % area.images.length),
      interval,
    );
    return () => clearInterval(t);
  }, [hover, area.images.length]);

  return (
    <motion.button
      onClick={() => onNavigate(area.to)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: index * 0.08 }}
      data-testid={`area-card-${area.key}`}
      className="group relative aspect-[4/5] rounded-2xl overflow-hidden text-left bg-slate-900 shadow-xl hover:shadow-2xl transition-shadow"
    >
      {/* Cross-fading images */}
      {area.images.map((src, i) => (
        <motion.img
          key={i}
          src={src}
          alt=""
          initial={false}
          animate={{
            opacity: imgIdx === i ? 1 : 0,
            scale: imgIdx === i ? (hover ? 1.12 : 1.02) : 1.0,
          }}
          transition={{
            opacity: { duration: 0.9, ease: "easeInOut" },
            scale: { duration: hover ? 1.5 : 4, ease: "linear" },
          }}
          className="absolute inset-0 w-full h-full object-cover"
        />
      ))}

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-slate-950/10 group-hover:from-slate-950 group-hover:via-slate-950/70 transition-colors" />

      {/* Foreground content */}
      <div className="relative h-full flex flex-col justify-end p-7 text-white">
        <div className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-[2px] text-cyan-300 mb-3">
          <span className="w-6 h-px bg-cyan-300" />
          {area.label}
        </div>
        <h3 className="font-display text-2xl md:text-[26px] leading-[1.15] font-bold mb-3 group-hover:text-cyan-200 transition-colors">
          {area.title}
        </h3>
        <p className="text-sm text-slate-300 leading-relaxed mb-5 opacity-90 line-clamp-3">
          {area.copy}
        </p>
        <div className="inline-flex items-center gap-2 text-sm font-bold text-cyan-300 group-hover:gap-3 transition-all">
          Learn more
          <ArrowRight className="w-4 h-4" />
        </div>
      </div>
    </motion.button>
  );
}

// ============================================================================
// TRUST MARQUEE
// ============================================================================
export function TrustMarquee() {
  const stream = [...TRUST_ITEMS, ...TRUST_ITEMS]; // doubled for seamless loop
  return (
    <section
      className="relative border-y border-amber-400/10 bg-[#142a4d]/85 py-5 overflow-hidden"
      data-testid="trust-marquee"
    >
      <div className="flex items-center gap-3 px-6 lg:px-12 mb-3 max-w-[1400px] mx-auto">
        <BadgeCheck className="w-4 h-4 text-amber-400" />
        <span className="text-[10px] font-bold tracking-[0.22em] uppercase text-amber-300 font-sans">
          Credentials · Clients · Compliance
        </span>
      </div>
      <div className="relative">
        <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-[#0d2042] to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-[#0d2042] to-transparent z-10 pointer-events-none" />
        <div className="flex gap-10 animate-marquee whitespace-nowrap w-max">
          {stream.map((item, i) => (
            <div key={`${item}-${i}`} className="flex items-center gap-3 shrink-0">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400/70" />
              <span className="font-display text-sm font-medium text-slate-300 tracking-wide">
                {item}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// THE MARRIAGE — Bento Grid
// ============================================================================
export function MarriageBento() {
  return (
    <section
      className="relative py-24 md:py-32 bg-gradient-to-b from-[#0d2042] to-[#091a36]"
      data-testid="marriage-bento"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={STAGGER}
          className="grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-6"
        >
          {/* Section headline (spans full top row) */}
          <motion.div variants={FADE_UP} className="lg:col-span-12 mb-2">
            <span className="text-[10px] font-bold tracking-[0.28em] uppercase text-cyan-300 font-sans">
              TWO BRANDS  ·  ONE OPERATING SYSTEM
            </span>
            <h2 className="mt-3 font-display text-4xl md:text-5xl font-semibold tracking-tight text-white max-w-3xl leading-[1.05]">
              Sterile Mate ships it.
              <br />
              <span className="text-cyan-300">AskIsaac</span> orchestrates it.
            </h2>
            <p className="mt-4 text-slate-400 max-w-2xl font-sans text-lg leading-relaxed">
              We are not another distributor catalog. Sterile Mate is the Certified Small Business
              supplier and licensed general contractor. AskIsaac is the AI-powered
              dispatch layer that makes every order, work-order, and compliance check
              run at hospital speed.
            </p>
          </motion.div>

          {/* Sterile Mate — physical supply (tall) */}
          <motion.div
            variants={SLIDE_LEFT}
            className="lg:col-span-5 lg:row-span-2 group rounded-3xl overflow-hidden border border-white/[0.06] relative min-h-[480px] bg-[#0b1d3a] hover:border-amber-400/30 transition-all"
            data-testid="marriage-sterilemate-card"
          >
            <img
              src={IMG_WAREHOUSE}
              alt="Sterile Mate physical supply"
              className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-55 transition-opacity"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#061124] via-[#061124]/80 to-transparent" />
            <div className="relative h-full flex flex-col justify-between p-8">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/15 border border-amber-400/30">
                  <Building2 className="w-3 h-3 text-amber-300" />
                  <span className="text-[10px] font-bold tracking-[0.22em] uppercase text-amber-300 font-sans">
                    STERILE MATE — THE SUPPLIER
                  </span>
                </div>
                <h3 className="mt-5 font-display text-3xl md:text-4xl text-white font-semibold leading-tight">
                  Physical supply.
                  <br />
                  Skilled trades.
                  <br />
                  Real people.
                </h3>
              </div>
              <div>
                <ul className="space-y-2.5 text-slate-200 font-sans">
                  {[
                    "Surgical instruments · ENT · Water systems · Dialysis",
                    "Licensed GC — electrical, plumbing, construction",
                    "EVS / janitorial disposables & infection control",
                    "small business + Certified Small Business — ready for public contracts",
                  ].map((l) => (
                    <li key={l} className="flex items-start gap-2.5 text-sm">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 text-amber-300 shrink-0" />
                      {l}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/solutions"
                  className="inline-flex items-center gap-2 mt-6 text-amber-300 hover:text-amber-200 text-sm font-semibold tracking-wide transition-colors"
                  data-testid="marriage-sterilemate-cta"
                >
                  Explore our solutions{" "}
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          </motion.div>

          {/* AskIsaac — intelligence (tall) */}
          <motion.div
            variants={SLIDE_RIGHT}
            className="lg:col-span-7 lg:row-span-2 group rounded-3xl overflow-hidden border border-white/[0.06] relative min-h-[480px] bg-gradient-to-br from-[#0b1d3a] to-[#061124] hover:border-cyan-400/40 transition-all"
            data-testid="marriage-askisaac-card"
          >
            <img
              src={IMG_DATA}
              alt="AskIsaac intelligence"
              className="absolute inset-0 w-full h-full object-cover opacity-25 group-hover:opacity-35 transition-opacity"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#061124] via-[#061124]/70 to-transparent" />
            <div className="absolute top-6 right-6 w-48 h-48 bg-cyan-500/20 blur-3xl rounded-full" />

            <div className="relative h-full flex flex-col justify-between p-8">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/15 border border-cyan-400/40">
                  <Cpu className="w-3 h-3 text-cyan-300" />
                  <span className="text-[10px] font-bold tracking-[0.22em] uppercase text-cyan-300 font-sans">
                    ASKISAAC — YOUR INTELLIGENCE WORKHORSE
                  </span>
                </div>
                <h3 className="mt-5 font-display text-3xl md:text-4xl text-white font-semibold leading-tight">
                  Dispatched <span className="text-cyan-300">on your</span>
                  <br />
                  hospital's behalf.
                </h3>
                <p className="mt-3 text-slate-300 font-sans max-w-lg leading-relaxed">
                  AskIsaac is not a catalog or a chatbot — it's the command layer
                  your facility runs on. Every RFQ, backorder, work order, and
                  compliance check is orchestrated 24/7 in the background.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3 mt-6">
                {[
                  { icon: Network, k: "37s", l: "Quote turnaround" },
                  { icon: Activity, k: "24/7", l: "Live dispatch" },
                  { icon: ShieldCheck, k: "100%", l: "Compliance tracked" },
                  { icon: Truck, k: "0", l: "Middlemen markups" },
                ].map((m) => (
                  <div
                    key={m.l}
                    className="rounded-xl bg-white/[0.03] border border-white/[0.08] px-4 py-3 backdrop-blur"
                  >
                    <m.icon className="w-4 h-4 text-cyan-300 mb-1.5" />
                    <div className="font-display text-2xl font-bold text-white tracking-tight">
                      {m.k}
                    </div>
                    <div className="text-[11px] text-slate-400 font-sans uppercase tracking-wider">
                      {m.l}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6">
                <Link
                  to="/how-it-works"
                  className="inline-flex items-center gap-2 text-cyan-300 hover:text-cyan-200 text-sm font-semibold tracking-wide transition-colors"
                  data-testid="marriage-askisaac-cta"
                >
                  How AskIsaac dispatches your orders{" "}
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          </motion.div>

          {/* Synthesis callout (wide) */}
          <motion.div
            variants={FADE_UP}
            className="lg:col-span-12 rounded-3xl border border-cyan-400/20 bg-gradient-to-r from-[#061124] via-[#0b1d3a] to-[#061124] p-7 md:p-10 flex flex-col md:flex-row md:items-center gap-6 relative overflow-hidden"
            data-testid="marriage-synthesis"
          >
            <div className="absolute -left-20 top-1/2 -translate-y-1/2 w-80 h-80 bg-cyan-500/15 blur-[120px] rounded-full pointer-events-none" />
            <div className="absolute -right-20 top-1/2 -translate-y-1/2 w-80 h-80 bg-amber-500/10 blur-[120px] rounded-full pointer-events-none" />

            <div className="relative flex-1">
              <span className="text-[10px] font-bold tracking-[0.28em] uppercase text-slate-500 font-sans">
                THE MARRIAGE
              </span>
              <h4 className="mt-2 font-display text-2xl md:text-3xl text-white font-semibold leading-snug tracking-tight">
                <span className="text-amber-300">Physical supply chain</span>{" "}
                +{" "}
                <span className="text-cyan-300">AI-powered dispatch</span>{" "}
                = the first true command center for healthcare facilities.
              </h4>
            </div>
            <div className="relative flex gap-3 shrink-0">
              <Link to="/hospital/login" data-testid="marriage-synthesis-cta">
                <Button className="btn-sheen h-12 px-6 bg-cyan-500 hover:bg-cyan-400 text-[#061124] font-bold rounded-xl">
                  Activate a Hospital
                </Button>
              </Link>
              <Link to="/join-network">
                <Button
                  variant="outline"
                  className="h-12 px-6 bg-transparent border-white/15 text-white hover:bg-white/5 font-semibold rounded-xl"
                  data-testid="marriage-join-cta"
                >
                  Join the Network
                </Button>
              </Link>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

// ============================================================================
// CATEGORY LEADER MANIFESTO
// ============================================================================
const MANIFESTO_PILLARS = [
  {
    k: "01",
    title: "We supply what hospitals need.",
    body:
      "Surgical instruments, ENT sets, AAMI ST108 water systems, EVS consumables, medical-grade construction — stocked, sourced, and delivered by a certified small business supplier.",
  },
  {
    k: "02",
    title: "We dispatch what they can't wait for.",
    body:
      "AskIsaac routes every RFQ, work order, backorder, and compliance check 24/7 — with a 37-second median quote turnaround and zero middlemen.",
  },
  {
    k: "03",
    title: "We stand behind every order.",
    body:
      "Licensed general contractor. ICRA-trained crews. Joint Commission-ready documentation. Signed off by the same people who pick up the phone when you call.",
  },
];

export function CategoryManifesto() {
  return (
    <section
      className="relative py-24 md:py-32 bg-[#061124] overflow-hidden"
      data-testid="category-manifesto"
    >
      {/* Ambient wash */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] bg-cyan-500/[0.06] blur-[140px] rounded-full" />
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(255,255,255,0.25) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.25) 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={STAGGER}
          className="max-w-4xl mx-auto text-center"
        >
          <motion.div variants={FADE_UP} className="flex items-center justify-center gap-3 mb-6">
            <div className="h-px w-10 bg-amber-400/50" />
            <span className="text-[10px] font-bold tracking-[0.32em] uppercase text-amber-300 font-sans">
              OUR MANIFESTO
            </span>
            <div className="h-px w-10 bg-amber-400/50" />
          </motion.div>

          <motion.h2
            variants={FADE_UP}
            className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.03]"
          >
            Three promises that define
            <br />
            <span className="text-cyan-300">this new category.</span>
          </motion.h2>

          <motion.p
            variants={FADE_UP}
            className="mt-6 text-slate-400 font-sans text-lg leading-relaxed max-w-2xl mx-auto"
          >
            Not a marketing line. Not a value-prop slide. The three commitments
            every Sterile Mate order, every AskIsaac dispatch, and every crew on
            site is held to — signed by the people accountable for them.
          </motion.p>
        </motion.div>

        {/* Three pillars */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={STAGGER}
          className="mt-16 grid md:grid-cols-3 gap-5 lg:gap-6"
        >
          {MANIFESTO_PILLARS.map((p, idx) => (
            <motion.div
              key={p.k}
              variants={FADE_UP}
              className="relative group rounded-2xl border border-white/[0.07] bg-white/[0.02] backdrop-blur-xl p-7 lg:p-8 hover:border-cyan-400/30 hover:-translate-y-0.5 transition-all duration-300"
              data-testid={`manifesto-pillar-${idx + 1}`}
            >
              {/* Number watermark */}
              <div
                className="absolute top-4 right-5 font-display text-[90px] font-bold text-white/[0.04] leading-none select-none pointer-events-none"
                aria-hidden
              >
                {p.k}
              </div>

              <div className="relative">
                <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.22em] uppercase text-cyan-300 font-sans">
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                  Promise {p.k}
                </span>

                <h3 className="mt-5 font-display text-2xl lg:text-[1.65rem] font-semibold text-white leading-tight tracking-tight">
                  {p.title}
                </h3>

                <p className="mt-4 text-slate-400 font-sans text-sm leading-relaxed">
                  {p.body}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Signature block */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={STAGGER}
          className="mt-14 flex flex-col items-center"
          data-testid="manifesto-signature"
        >
          {/* Handwritten-style signature (SVG path — no image dependency) */}
          <svg
            viewBox="0 0 340 80"
            className="w-[260px] md:w-[320px] h-auto"
            aria-hidden
          >
            <path
              d="M8 52 C 22 18, 40 14, 48 42 S 70 70, 82 38 S 110 10, 118 46 Q 128 70, 142 40 T 172 38 Q 186 38, 196 28 Q 208 14, 220 44 Q 232 72, 246 36 Q 258 8, 272 40 Q 282 62, 298 30 Q 310 10, 330 34"
              fill="none"
              stroke="#22d3ee"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity="0.9"
            />
            {/* Underline flourish */}
            <path
              d="M16 68 Q 170 76, 326 66"
              fill="none"
              stroke="#f59e0b"
              strokeWidth="1.5"
              strokeLinecap="round"
              opacity="0.7"
            />
          </svg>

          <div className="mt-5 text-center">
            <p className="font-display text-base text-white font-semibold tracking-tight">
              George Broughton III
            </p>
            <p className="mt-1 text-[11px] font-sans tracking-[0.22em] uppercase text-slate-500">
              Founder &amp; CEO  ·  Sterile Mate LLC
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ============================================================================
// VS DISTRIBUTORS
// ============================================================================
const DISTRIBUTOR_ROWS = [
  { dim: "Supply catalog",            them: "Millions of SKUs, impersonal",  us: "Curated + sourced for you" },
  { dim: "Skilled-trade access",       them: "Separate vendors, no ownership", us: "Licensed GC under one roof" },
  { dim: "Quote turnaround",           them: "2–5 business days",              us: "37-second median via AskIsaac" },
  { dim: "Minimum order / contracts",  them: "GPO-gated, heavy minimums",      us: "None — order what you need" },
  { dim: "Compliance documentation",   them: "Manual, request-only",           us: "Audit-ready, attached to every order" },
  { dim: "certified small business diversity credit", them: "Distributor-only",               us: "Dual role — supplier + trades" },
  { dim: "Facility-level intelligence",them: "None — transactional",           us: "AskIsaac orchestrates 24/7" },
];

export function VsDistributors() {
  return (
    <section
      className="relative py-24 md:py-32 bg-gradient-to-b from-[#061124] to-[#0b1d3a] overflow-hidden"
      data-testid="vs-distributors"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={STAGGER}
          className="grid lg:grid-cols-[1fr_1.4fr] gap-10 lg:gap-16 items-start"
        >
          <motion.div variants={FADE_UP}>
            <span className="text-[10px] font-bold tracking-[0.28em] uppercase text-amber-300 font-sans">
              A NEW CATEGORY
            </span>
            <h2 className="mt-3 font-display text-4xl md:text-5xl font-semibold tracking-tight text-white leading-[1.05]">
              Legacy distribution moves
              <span className="text-slate-500"> boxes.</span>
              <br />
              We move <span className="text-cyan-300">the whole operation.</span>
            </h2>
            <p className="mt-5 text-slate-400 font-sans leading-relaxed max-w-md">
              The old model built catalogs. We built a command center — a
              dual-certified small business supplier fused with an AI dispatch layer working
              for the hospital, not the vendor. That is structurally different,
              and structurally better.
            </p>

            <div className="mt-8 flex flex-wrap gap-2">
              {[
                "small business + small business Supplier",
                "Licensed GC Under One Roof",
                "AI-Native Dispatch",
                "Hospital-Aligned, Not Vendor-Aligned",
              ].map((n) => (
                <span
                  key={n}
                  className="px-3 py-1.5 text-[11px] font-sans font-medium tracking-wide text-slate-300 bg-white/[0.03] border border-white/[0.08] rounded-full"
                >
                  {n}
                </span>
              ))}
            </div>
          </motion.div>

          {/* Comparison table */}
          <motion.div
            variants={FADE_UP}
            className="rounded-3xl border border-white/[0.08] bg-white/[0.02] backdrop-blur-xl overflow-hidden shadow-[0_30px_80px_-30px_rgba(0,0,0,0.7)]"
            data-testid="vs-distributors-table"
          >
            {/* Header */}
            <div className="grid grid-cols-[1.2fr_1fr_1fr] gap-4 px-5 md:px-7 py-4 border-b border-white/[0.08] bg-white/[0.03]">
              <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500 font-sans">
                Dimension
              </span>
              <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500 font-sans">
                The Legacy Model
              </span>
              <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-cyan-300 font-sans flex items-center gap-1">
                <Zap className="w-3 h-3" /> Sterile Mate + AskIsaac
              </span>
            </div>
            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
              variants={ROW_STAGGER}
            >
              {DISTRIBUTOR_ROWS.map((r, i) => (
                <motion.div
                  key={r.dim}
                  variants={ROW_FADE}
                  className={`grid grid-cols-[1.2fr_1fr_1fr] gap-4 px-5 md:px-7 py-4 items-center ${
                    i !== DISTRIBUTOR_ROWS.length - 1
                      ? "border-b border-white/[0.05]"
                      : ""
                  }`}
                >
                  <span className="font-display text-white text-sm font-medium">
                    {r.dim}
                  </span>
                  <span className="flex items-start gap-2 text-slate-400 font-sans text-sm">
                    <XCircle className="w-4 h-4 mt-0.5 text-rose-500/80 shrink-0" />
                    {r.them}
                  </span>
                  <span className="flex items-start gap-2 text-white font-sans text-sm">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 text-cyan-400 shrink-0" />
                    {r.us}
                  </span>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}


// ============================================================================
// SOLUTIONS GRID — Medline / McKesson-style 6-tile featured solutions
// ============================================================================
const SOLUTIONS = [
  {
    icon: Stethoscope,
    title: "Surgical Solutions",
    blurb:
      "Aspen, Sol-M, and partner-curated surgical instruments, sutures, scopes, and implants — sourced for the OR.",
    cta: "Explore Surgical",
    href: "/sterilemate/surgical",
    img:
      "https://images.unsplash.com/photo-1551601651-2a8555f1a136?w=900&q=80",
    accent: "from-cyan-400/10 to-cyan-500/5",
    iconBg: "bg-cyan-500/15 text-cyan-300",
  },
  {
    icon: Ear,
    title: "ENT Instruments",
    blurb:
      "208+ German- and American-made BEAR-ENT instruments. Otology, rhinology, laryngology, and procedural sets.",
    cta: "Browse ENT Catalog",
    href: "/sterilemate/ent-surgical",
    img:
      "https://images.unsplash.com/photo-1559757175-5700dde675bc?w=900&q=80",
    accent: "from-amber-400/10 to-amber-500/5",
    iconBg: "bg-amber-500/15 text-amber-300",
  },
  {
    icon: Sparkles,
    title: "Janitorial & EVS",
    blurb:
      "Authorized prime distributor for Filmop USA — Alpha Workstations, Omega carts, microfiber, Green Klean disposables.",
    cta: "Open Marketplace",
    href: "/hospital/marketplace",
    img:
      "https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=900&q=80",
    accent: "from-emerald-400/10 to-emerald-500/5",
    iconBg: "bg-emerald-500/15 text-emerald-300",
  },
  {
    icon: Droplets,
    title: "Water Treatment",
    blurb:
      "AAMI ST108-ready RO systems, commercial filtration & softening, dialysis concentrates — Puretech distribution partner.",
    cta: "See Water Solutions",
    href: "/solutions/water-treatment",
    img:
      "https://images.unsplash.com/photo-1538108149393-fbbd81895907?w=900&q=80",
    accent: "from-sky-400/10 to-sky-500/5",
    iconBg: "bg-sky-500/15 text-sky-300",
  },
  {
    icon: HardHat,
    title: "Healthcare General Contracting",
    blurb:
      "Licensed GC, ICRA-certified crews, electrical · plumbing · construction · repairs — one contractor, every discipline.",
    cta: "GC Capabilities",
    href: "/solutions/general-contracting",
    img:
      "https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=900&q=80",
    accent: "from-orange-400/10 to-orange-500/5",
    iconBg: "bg-orange-500/15 text-orange-300",
  },
  {
    icon: Cpu,
    title: "AskIsaac Software",
    blurb:
      "Implant management, rebate tracking, backorder routing, work-order dispatch — hospital intelligence at facility speed.",
    cta: "Explore AskIsaac Software",
    href: "/solutions#hart",
    img:
      "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=900&q=80",
    accent: "from-violet-400/10 to-violet-500/5",
    iconBg: "bg-violet-500/15 text-violet-300",
  },
];

export function SolutionsGrid() {
  return (
    <section
      className="relative py-24 md:py-28 bg-gradient-to-b from-[#0a1c3c] to-[#091a36]"
      data-testid="solutions-grid"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={STAGGER}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12"
        >
          <motion.div variants={FADE_UP} className="max-w-2xl">
            <span className="text-[10px] font-bold tracking-[0.28em] uppercase text-cyan-300 font-sans">
              SIX SOLUTIONS  ·  ONE COMMAND CENTER
            </span>
            <h2 className="mt-3 font-display text-4xl md:text-5xl font-semibold tracking-tight text-white leading-[1.05]">
              Everything healthcare runs on,
              <br />
              <span className="text-cyan-300">delivered as one system.</span>
            </h2>
          </motion.div>
          <motion.p
            variants={FADE_UP}
            className="text-slate-400 font-sans max-w-md leading-relaxed"
          >
            Surgical, ENT, water, EVS, construction, and AI dispatch — sourced,
            staffed, and supported by the same team that picks up your call.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={STAGGER}
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6"
        >
          {SOLUTIONS.map((s, i) => (
            <motion.div
              key={s.title}
              variants={FADE_UP}
              data-testid={`solution-${i + 1}`}
            >
              <Link
                to={s.href}
                className="group relative block h-full rounded-2xl overflow-hidden border border-white/[0.07] bg-[#0b1d3a] hover:border-cyan-400/40 hover:-translate-y-1 hover:shadow-2xl hover:shadow-cyan-500/10 hover:ring-1 hover:ring-cyan-400/30 transition-all duration-300"
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={s.img}
                    alt={s.title}
                    loading="lazy"
                    className="w-full h-full object-cover opacity-55 group-hover:opacity-75 group-hover:scale-105 transition-all duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0b1d3a] via-[#0b1d3a]/40 to-transparent" />
                  <div className={`absolute inset-0 bg-gradient-to-br ${s.accent} pointer-events-none`} />
                  <div className={`absolute top-4 left-4 w-10 h-10 rounded-xl ${s.iconBg} flex items-center justify-center backdrop-blur`}>
                    <s.icon className="w-5 h-5" />
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-display text-xl font-semibold text-white tracking-tight">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-sm text-slate-400 font-sans leading-relaxed line-clamp-3">
                    {s.blurb}
                  </p>
                  <span className="inline-flex items-center gap-1.5 mt-5 text-cyan-300 group-hover:text-cyan-200 text-sm font-semibold tracking-wide transition-colors">
                    {s.cta}
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ============================================================================
// METRICS BAND — Medline-style big stats
// ============================================================================
const METRICS = [
  { k: "208+", l: "ENT instruments in catalog", sub: "German & American made" },
  { k: "536", l: "Filmop USA SKUs in stock", sub: "Texas-based fulfillment" },
  { k: "37s", l: "Median quote turnaround", sub: "via AskIsaac dispatch" },
  { k: "5+ yr", l: "Texas Medical Center", sub: "Trusted since 2020" },
];

export function MetricsBand() {
  return (
    <section
      className="relative py-20 md:py-24 bg-gradient-to-b from-[#061124] via-[#0b1d3a] to-[#061124] border-y border-white/[0.05]"
      data-testid="metrics-band"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={STAGGER}
          className="grid md:grid-cols-4 gap-8 md:gap-4"
        >
          {METRICS.map((m, i) => (
            <motion.div
              key={m.l}
              variants={FADE_UP}
              className={`relative ${
                i !== 0 ? "md:border-l md:border-white/[0.08] md:pl-6" : ""
              }`}
              data-testid={`metric-${i + 1}`}
            >
              <div className="font-display text-5xl md:text-6xl font-bold text-white tracking-tight">
                {m.k}
              </div>
              <div className="mt-2 font-display text-base md:text-lg font-semibold text-slate-200 leading-snug">
                {m.l}
              </div>
              <div className="mt-1 text-xs text-cyan-300/80 font-sans tracking-wide">
                {m.sub}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ============================================================================
// INDUSTRIES WE SERVE — clean chip / icon strip
// ============================================================================
const INDUSTRIES = [
  { icon: Building2, label: "Hospitals & Health Systems" },
  { icon: Stethoscope, label: "Surgery Centers" },
  { icon: Ear, label: "ENT Practices" },
  { icon: Droplets, label: "Dialysis & Lab" },
  { icon: HardHat, label: "Public-Sector Capital Projects" },
  { icon: Sparkles, label: "EVS / Janitorial Operators" },
  { icon: FlaskConical, label: "Sterile Processing Depts" },
  { icon: Boxes, label: "Distributors & GPOs" },
];

export function IndustriesWeServe() {
  return (
    <section
      className="relative py-20 md:py-24 bg-[#061124]"
      data-testid="industries-served"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={STAGGER}
          className="text-center max-w-3xl mx-auto mb-12"
        >
          <motion.span
            variants={FADE_UP}
            className="inline-block text-[10px] font-bold tracking-[0.28em] uppercase text-amber-300 font-sans"
          >
            INDUSTRIES WE SERVE
          </motion.span>
          <motion.h2
            variants={FADE_UP}
            className="mt-3 font-display text-4xl md:text-5xl font-semibold tracking-tight text-white leading-[1.05]"
          >
            Built for every facility that
            <br />
            <span className="text-cyan-300">can't afford a delay.</span>
          </motion.h2>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={STAGGER}
          className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4"
        >
          {INDUSTRIES.map((ind, i) => (
            <motion.div
              key={ind.label}
              variants={FADE_UP}
              className="group flex items-center gap-3 px-5 py-4 rounded-xl border border-white/[0.07] bg-white/[0.02] backdrop-blur-xl hover:border-cyan-400/30 hover:bg-white/[0.04] transition-all"
              data-testid={`industry-${i + 1}`}
            >
              <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center shrink-0 group-hover:bg-cyan-500/20 transition-colors">
                <ind.icon className="w-5 h-5 text-cyan-300" />
              </div>
              <span className="font-display text-sm md:text-base text-white font-medium leading-tight">
                {ind.label}
              </span>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ============================================================================
// NEWS & INSIGHTS — 3-card press / blog feed (matches Medline + McKesson)
// ============================================================================
const NEWS_ITEMS = [
  {
    tag: "Press Release",
    date: "Nov 11, 2025",
    title:
      "Sterile Mate Unveils Healthcare Software Division — AskIsaac Powers Hospital Operational Efficiency",
    img:
      "https://sterilemate.com/wp-content/uploads/2025/11/freepik__the-style-is-candid-image-photography-with-natural__39618-233x300.jpeg",
    href:
      "https://sterilemate.com/sterile-mate-llc-unveils-new-healthcare-software-division-in-partnership-with-techinquest-to-advance-hospital-financial-and-operational-efficiency/",
  },
  {
    tag: "Capabilities",
    date: "Oct 30, 2025",
    title:
      "Sterile Mate Expands Water Treatment & Chemical Division — AAMI ST108 Solutions for Healthcare",
    img:
      "https://sterilemate.com/wp-content/uploads/2025/11/water-quality-specialists-analyzing-water-300x200.webp",
    href:
      "https://sterilemate.com/sterile-mate-expands-water-treatment-chemical-division/",
  },
  {
    tag: "Industry Insight",
    date: "Nov 7, 2025",
    title:
      "Medical Research Spotlight — Breakthroughs Driving Hospital Procurement Decisions",
    img:
      "https://sterilemate.com/wp-content/uploads/2025/11/freepik__-freepik-image-description-breakthroughs-in-kidney__72267-300x300.jpeg",
    href: "https://sterilemate.com/medical-research-spotlight/",
  },
];

export function NewsAndInsights() {
  return (
    <section
      className="relative py-24 md:py-28 bg-gradient-to-b from-[#061124] to-[#0b1d3a]"
      data-testid="news-insights"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={STAGGER}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12"
        >
          <motion.div variants={FADE_UP}>
            <span className="text-[10px] font-bold tracking-[0.28em] uppercase text-cyan-300 font-sans flex items-center gap-2">
              <Newspaper className="w-3 h-3" /> NEWS &amp; INSIGHTS
            </span>
            <h2 className="mt-3 font-display text-4xl md:text-5xl font-semibold tracking-tight text-white leading-[1.05]">
              The latest from
              <br />
              <span className="text-cyan-300">Sterile Mate &amp; AskIsaac.</span>
            </h2>
          </motion.div>
          <motion.a
            variants={FADE_UP}
            href="https://sterilemate.com/daily-medical-news/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-cyan-300 hover:text-cyan-200 font-semibold tracking-wide text-sm transition-colors inline-flex items-center gap-1.5"
            data-testid="news-see-all"
          >
            See all press &amp; insights{" "}
            <ArrowRight className="w-4 h-4" />
          </motion.a>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={STAGGER}
          className="grid md:grid-cols-3 gap-5 lg:gap-6"
        >
          {NEWS_ITEMS.map((n, i) => (
            <motion.a
              key={n.title}
              variants={FADE_UP}
              href={n.href}
              target="_blank"
              rel="noopener noreferrer"
              className="group block rounded-2xl overflow-hidden border border-white/[0.07] bg-[#0b1d3a] hover:border-cyan-400/40 hover:-translate-y-1 hover:shadow-2xl hover:shadow-cyan-500/10 hover:ring-1 hover:ring-cyan-400/30 transition-all duration-300"
              data-testid={`news-card-${i + 1}`}
            >
              <div className="relative h-48 overflow-hidden bg-[#061124]">
                <img
                  src={n.img}
                  alt={n.title}
                  loading="lazy"
                  className="w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0b1d3a]/80 via-transparent to-transparent" />
                <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1 text-[10px] font-bold tracking-[0.18em] uppercase text-cyan-200 bg-cyan-500/15 backdrop-blur border border-cyan-400/30 rounded-full">
                  {n.tag}
                </span>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-1.5 text-[11px] font-sans text-slate-500 tracking-wide">
                  <Calendar className="w-3 h-3" /> {n.date}
                </div>
                <h3 className="mt-3 font-display text-lg font-semibold text-white leading-snug tracking-tight group-hover:text-cyan-200 transition-colors line-clamp-3">
                  {n.title}
                </h3>
                <span className="inline-flex items-center gap-1.5 mt-5 text-cyan-300 group-hover:text-cyan-200 text-sm font-semibold tracking-wide transition-colors">
                  Read more
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </span>
              </div>
            </motion.a>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
